"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface SavedTag {
  id: string
  tag: string
  frequency: number
  lastUsed: number
  category?: string
  createdAt: number
}

interface SavedTagsStore {
  savedTags: SavedTag[]
  addTag: (tag: string, category?: string) => void
  removeTag: (id: string) => void
  incrementFrequency: (tag: string) => void
  getRecentTags: (limit?: number) => SavedTag[]
  getFrequentTags: (limit?: number) => SavedTag[]
  getAllTags: () => SavedTag[]
  clearOldTags: (daysOld?: number) => void
}

export const useSavedTagsStore = create<SavedTagsStore>()(
  persist(
    (set, get) => ({
      savedTags: [],

      addTag: (tag: string, category?: string) => {
        const existingTag = get().savedTags.find((t) => t.tag === tag)
        
        if (existingTag) {
          // Update existing tag
          set((state) => ({
            savedTags: state.savedTags.map((t) =>
              t.tag === tag
                ? { ...t, frequency: t.frequency + 1, lastUsed: Date.now(), category: category || t.category }
                : t
            ),
          }))
        } else {
          // Add new tag
          const newTag: SavedTag = {
            id: crypto.randomUUID(),
            tag,
            frequency: 1,
            lastUsed: Date.now(),
            category,
            createdAt: Date.now(),
          }
          set((state) => ({
            savedTags: [...state.savedTags, newTag],
          }))
        }
      },

      removeTag: (id: string) => {
        set((state) => ({
          savedTags: state.savedTags.filter((t) => t.id !== id),
        }))
      },

      incrementFrequency: (tag: string) => {
        set((state) => ({
          savedTags: state.savedTags.map((t) =>
            t.tag === tag
              ? { ...t, frequency: t.frequency + 1, lastUsed: Date.now() }
              : t
          ),
        }))
      },

      getRecentTags: (limit = 10) => {
        return get().savedTags
          .sort((a, b) => b.lastUsed - a.lastUsed)
          .slice(0, limit)
      },

      getFrequentTags: (limit = 10) => {
        return get().savedTags
          .sort((a, b) => b.frequency - a.frequency)
          .slice(0, limit)
      },

      getAllTags: () => {
        return get().savedTags.sort((a, b) => a.tag.localeCompare(b.tag))
      },

      clearOldTags: (daysOld = 30) => {
        const cutoffDate = Date.now() - (daysOld * 24 * 60 * 60 * 1000)
        set((state) => ({
          savedTags: state.savedTags.filter((t) => t.lastUsed > cutoffDate || t.frequency > 5),
        }))
      },
    }),
    {
      name: "saved-tags",
    }
  )
)